import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Btn, DangerBtn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs, TextInput, fmtDate,
  panelPost, useAdminGet,
} from './kit';
import type { AdminTargetIn } from './adminNav';
import { type Period, PeriodPicker, periodError, periodText } from './period';
import { AdminLink } from './adminNav';

/**
 * /admin → API spend (Backend/api_spend.py, section "spend"): what the AI
 * APIs cost — today, by day, by month and in all.
 *
 *  - Overview: today, 7 days, this month and the chosen period; daily spend
 *    stacked by what it paid for (Cosmos search, Horizon, OCEAN, For You,
 *    tech enrichment, other), by month.
 *
 * One period (top right) drives every tab: presets or a custom from–to, in
 * India days. Horizon runs on the old Azure bulk key are listed, not counted.
 *  - Replicate: every logged call by area and step, by model, the account's
 *    predictions no call here logged, and the sync with Replicate's record.
 *  - Horizon: every run's cost, by month, and which key paid — recorded by
 *    the run, or set here with a note.
 *  - Members: whose searches used the most (searches counted from when
 *    per-call logging began, like the costs).
 *  - Per search: what one search costs by kind (quick, AI, deep, filters
 *    only, cache, homepage), members vs signed-out, OCEAN searches and
 *    problem → technology matches, and by day with running averages.
 *  - Manual costs: costs billed outside what is logged (a university's tech
 *    enrichment, a Horizon run elsewhere, an invoice), counted in every total.
 *  - Credits: prepaid top-ups per provider and the balance left — spend draws
 *    down the top-up that expires first; leftovers past expiry count as expired.
 *  - Keys & prices: an owner / use / notes record per API key (never the key
 *    itself), and the per-model token prices the costs are worked out with.
 *
 * Deep links: /admin?group=spend&tab=<overview|replicate|horizon|members|per_search|manual|credits|keys>.
 */

const S = 'spend';
const TABS = ['Overview', 'Replicate', 'Horizon', 'Members', 'Per search', 'Manual costs', 'Credits', 'Keys & prices'];
const TAB_PARAM: Record<string, string> = {
  overview: 'Overview', replicate: 'Replicate', horizon: 'Horizon', members: 'Members', users: 'Members',
  per_search: 'Per search', cost_per_search: 'Per search',
  manual: 'Manual costs', manual_costs: 'Manual costs', credits: 'Credits', balance: 'Credits',
  keys: 'Keys & prices', keys_prices: 'Keys & prices', prices: 'Keys & prices',
};
const PRESETS = ['7d', '30d', '90d', 'this_month', 'last_month', '365d', 'custom'];
const PRESET_LABELS: Record<string, string> = {
  '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', this_month: 'This month',
  last_month: 'Last month', '365d': 'Last 365 days', custom: 'Custom range',
};
const PRESET_DAYS: Record<string, number | null> = { '7d': 7, '30d': 30, '90d': 90, '365d': 365, this_month: null, last_month: null };
/** Today in India — the server cuts days in IST. */
const istToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const periodParams = (p: Period): Record<string, string> => {
  if (p.preset === 'custom') return { ...(p.from ? { from: p.from } : {}), ...(p.to ? { to: p.to } : {}) };
  const t = istToday();
  if (p.preset === 'this_month') return { from: `${t.slice(0, 7)}-01`, to: t };
  if (p.preset === 'last_month') {
    const [y, m] = t.split('-').map(Number);
    const py = m === 1 ? y - 1 : y, pm = m === 1 ? 12 : m - 1;
    const last = new Date(Date.UTC(py, pm, 0)).getUTCDate();
    const mm = String(pm).padStart(2, '0');
    return { from: `${py}-${mm}-01`, to: `${py}-${mm}-${String(last).padStart(2, '0')}` };
  }
  return { days: String(PRESET_DAYS[p.preset] || 30) };
};
type TabProps = { tick: number; bump: () => void; params: Record<string, string>; label: string };

// Chart groups: fixed hue per group (never by rank). Validated categorical
// slots 1-5; everything else folds into a neutral "Other".
const GROUPS: { key: string; label: string; color: string; areas: string[] }[] = [
  { key: 'search', label: 'Cosmos search', color: '#2a78d6', areas: ['search'] },
  { key: 'horizon', label: 'Horizon', color: '#eb6834', areas: ['horizon'] },
  { key: 'ocean', label: 'OCEAN', color: '#1baf7a', areas: ['ocean', 'ocean_match'] },
  { key: 'foryou', label: 'For You & onboarding', color: '#eda100', areas: ['foryou', 'onboarding'] },
  { key: 'enrichment', label: 'Tech enrichment', color: '#e87ba4', areas: ['enrichment', 'recrawl'] },
  { key: 'rest', label: 'Other', color: '#9a9a94', areas: [] },
];
const groupOf = (area: string) => (GROUPS.find(g => g.areas.includes(area)) || GROUPS[GROUPS.length - 1]).key;

export const usd = (v: any): string => {
  const n = Number(v);
  if (v === null || v === undefined || v === '' || isNaN(n)) return '—';
  if (n === 0) return '$0';
  const a = Math.abs(n);
  const d = a >= 100 ? 0 : a >= 1 ? 2 : a >= 0.01 ? 3 : 4;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
};
const fmtDay = (iso?: string | null) => (iso
  ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
/** Small per-search amounts: two significant digits below a cent ($0.000075). */
const usdPer = (v: any): string => {
  const n = Number(v);
  if (v === null || v === undefined || v === '' || isNaN(n) || n === 0 || Math.abs(n) >= 0.01) return usd(v);
  return `$${Number(n.toPrecision(2)).toLocaleString('en-US', { maximumFractionDigits: 8 })}`;
};
const int = (v: any) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-US'));
const pct = (v: any) => (v === null || v === undefined ? '—' : `${v}%`);
const ago = (iso?: string | null) => {
  if (!iso) return 'never';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (isNaN(m)) return fmtDate(iso);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 48 * 60) return `${Math.round(m / 60)} h ago`;
  return fmtDate(iso);
};
const dayLabel = (s: string) => {
  const t = new Date(`${s}T00:00:00`);
  return isNaN(t.getTime()) ? s : t.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const CHART_PX = 176;   // h-44


// ---------------------------------------------------------------------------
// Daily stacked bars
// ---------------------------------------------------------------------------
const DailyStack: React.FC<{ daily: any[] }> = ({ daily }) => {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const [off, setOff] = useState<Record<string, boolean>>({});
  const all = useMemo(() => daily.map(d => {
    const g: Record<string, number> = {};
    Object.entries(d.parts || {}).forEach(([area, v]) => {
      const k = groupOf(area);
      g[k] = (g[k] || 0) + Number(v || 0);
    });
    return { day: String(d.day), groups: g };
  }), [daily]);
  const inData = GROUPS.filter(g => all.some(d => d.groups[g.key]));
  const present = inData.filter(g => !off[g.key]);
  // Hiding a group (click it in the legend) rescales the chart to the rest —
  // one big Horizon run otherwise flattens every Replicate day.
  const days = all.map(d => ({ ...d, total: present.reduce((a, g) => a + (d.groups[g.key] || 0), 0) }));
  const max = Math.max(0.0001, ...days.map(d => d.total));
  const shown = hover !== null ? days[hover] : null;
  const total = days.reduce((a, d) => a + d.total, 0);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 flex-wrap text-xs text-grayscale-1200">
          {inData.map(g => (
            <button key={g.key} type="button" onClick={() => setOff(o => ({ ...o, [g.key]: !o[g.key] }))}
              title={off[g.key] ? `Show ${g.label}` : `Hide ${g.label}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 hover:bg-grayscale-200 ${off[g.key] ? 'opacity-40 line-through' : ''}`}>
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: g.color }} />{g.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-grayscale-1100" aria-live="polite">
            {shown
              ? <><span className="font-bold text-grayscale-1300">{usd(shown.total)}</span> on {dayLabel(shown.day)}</>
              : <>Total <span className="font-bold text-grayscale-1300">{usd(total)}</span> over {days.length} days</>}
          </div>
          <Btn small onClick={() => setAsTable(v => !v)}>{asTable ? 'Chart' : 'Table'}</Btn>
        </div>
      </div>
      {asTable ? (
        <DataTable csvName="api_spend_daily" defaultSort="-day"
          rows={days.map(d => ({ day: d.day, ...Object.fromEntries(present.map(g => [g.key, d.groups[g.key] || 0])),
            total: d.total }))}
          columns={['day', ...present.map(g => g.key), 'total']}
          labels={{ day: 'Day', total: 'Total', ...Object.fromEntries(present.map(g => [g.key, g.label])) }}
          render={{ total: r => usd(r.total), ...Object.fromEntries(present.map(g => [g.key, (r: any) => usd(r[g.key])])) }} />
      ) : (
        <div className="flex gap-2">
          <div className="flex flex-col justify-between text-[10px] text-grayscale-1100 text-right w-14 shrink-0 h-44">
            <span>{usd(max)}</span><span>{usd(max / 2)}</span><span>$0</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="relative h-44 border-b border-grayscale-300" onMouseLeave={() => setHover(null)}>
              <div aria-hidden className="absolute inset-x-0 top-0 border-t border-dashed border-grayscale-200" />
              <div aria-hidden className="absolute inset-x-0 top-1/2 border-t border-dashed border-grayscale-200" />
              <div className="absolute inset-0 flex items-end gap-[2px]">
                {days.map((d, i) => {
                  const segs = present.filter(g => d.groups[g.key]);
                  return (
                    <div key={d.day} className="flex-1 h-full flex flex-col justify-end cursor-default relative"
                      onMouseEnter={() => setHover(i)}>
                      <div className={`w-full flex flex-col-reverse ${hover === i ? 'opacity-80' : ''}`}>
                        {segs.map((g, j) => {
                          const px = (d.groups[g.key] / max) * CHART_PX;
                          return (
                            <div key={g.key} className={j === segs.length - 1 ? 'rounded-t' : ''}
                              style={{ height: Math.max(px, 1), background: g.color,
                                // 2px surface gap between stacked segments, where there is room for it
                                borderTop: j > 0 && px > 4 ? '2px solid #fff' : undefined, boxSizing: 'border-box' }} />
                          );
                        })}
                      </div>
                      {hover === i && d.total > 0 && (
                        <div className={`absolute bottom-full mb-1 z-10 bg-white border border-grayscale-300 rounded-lg shadow px-2.5 py-1.5
                          text-xs whitespace-nowrap ${i > days.length / 2 ? 'right-0' : 'left-0'}`}>
                          <div className="font-bold text-grayscale-1300 mb-0.5">{dayLabel(d.day)} · {usd(d.total)}</div>
                          {segs.slice().reverse().map(g => (
                            <div key={g.key} className="flex items-center gap-1.5 text-grayscale-1200">
                              <span className="w-2 h-2 rounded-sm" style={{ background: g.color }} />
                              <span className="flex-1">{g.label}</span>
                              <span className="text-grayscale-1300 font-medium ml-3">{usd(d.groups[g.key])}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-between text-[10px] text-grayscale-1100 mt-1">
              <span>{days[0] ? dayLabel(days[0].day) : ''}</span>
              <span>{days.length ? dayLabel(days[days.length - 1].day) : ''}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sync line (Replicate's own record)
// ---------------------------------------------------------------------------
const SyncLine: React.FC<{ sync: any; onSynced: () => void }> = ({ sync, onSynced }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const accounts: any[] = sync?.accounts || [];
  const errors: string[] = sync?.last_run?.errors || [];
  const run = async () => {
    setBusy(true); setErr('');
    const { data, error } = await panelPost(S, 'sync_now', {});
    setBusy(false);
    if (error) setErr(error);
    else if (data?.errors?.length) setErr(data.errors.join(' · '));
    onSynced();
  };
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1100">
      <span>
        Replicate's record {accounts.length
          ? <>read {ago(sync?.last_run?.at)}{accounts.map(a => (
            <span key={a.account}> · account <b className="text-grayscale-1300">{a.account}</b>
              {a.key_last4 ? <> (key …{a.key_last4})</> : null}
              {a.oldest ? <>, from {new Date(a.oldest).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</> : null}
              {!a.history_complete ? <>, older history still loading</> : null}</span>))}</>
          : <>not read yet</>}
      </span>
      <Btn small onClick={run} disabled={busy}>{busy ? 'Syncing…' : 'Sync now'}</Btn>
      {(err || errors.length > 0) && <span className="text-red-600">{err || errors.join(' · ')}</span>}
      {accounts.some(a => a.gap_note) && <span className="text-amber-700">{accounts.find(a => a.gap_note)?.gap_note}</span>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------
const Overview: React.FC<TabProps> = ({ tick, bump, params, label }) => {
  const r = useAdminGet(S, 'overview', params, [JSON.stringify(params), tick]);
  const d: any = r.data;
  const k = d?.kpis || {};
  const hint = (x: any) => (x ? `Replicate ${usd(x.replicate)} · Horizon ${usd(x.horizon)}${x.other ? ` · Other ${usd(x.other)}` : ''}` : '');
  return (
    <div className="flex flex-col gap-4">
      {r.error && <Msg kind="error">{r.error}</Msg>}
      {!d && r.loading ? <Loading /> : d && (
        <>
          <KPIRow>
            <KPI label="Today" value={usd(k.today?.total)} hint={hint(k.today)} />
            <KPI label="Last 7 days" value={usd(k.last_7?.total)} hint={hint(k.last_7)} />
            <KPI label="This month" value={usd(k.this_month?.total)} hint={hint(k.this_month)} />
            <KPI label={label} value={usd(k.period?.total)} hint={hint(k.period)} />
          </KPIRow>
          <Section title={`Daily spend · ${label}`}>
            <DailyStack daily={d.daily || []} />
            <SyncLine sync={d.sync} onSynced={bump} />
            {d.unpriced?.calls > 0 && (
              <div className="text-xs text-amber-700">
                {int(d.unpriced.calls)} {d.unpriced.calls === 1 ? 'call' : 'calls'} on models with no price {d.unpriced.calls === 1 ? 'is' : 'are'} not in the totals ({d.unpriced.models.join(', ')}) — add
                their price in Keys & prices.
              </div>
            )}
          </Section>
          <Section title="By month">
            <DataTable csvName="api_spend_monthly" rows={d.monthly || []} defaultSort="-month"
              columns={['month', 'replicate', 'horizon', 'other', 'total']}
              labels={{ month: 'Month', replicate: 'Replicate', horizon: 'Horizon', other: 'Other', total: 'Total' }}
              render={{ replicate: x => usd(x.replicate), horizon: x => usd(x.horizon), other: x => usd(x.other),
                total: x => <b>{usd(x.total)}</b> }}
              empty="No spend recorded yet." />
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Replicate
// ---------------------------------------------------------------------------
const Replicate: React.FC<TabProps> = ({ tick, bump, params, label }) => {
  const r = useAdminGet(S, 'replicate', params, [JSON.stringify(params), tick]);
  const d: any = r.data;
  return (
    <div className="flex flex-col gap-4">
      {r.error && <Msg kind="error">{r.error}</Msg>}
      <div className="flex items-center gap-3 flex-wrap">
        {d && <SyncLine sync={d.sync} onSynced={bump} />}
      </div>
      {!d && r.loading ? <Loading /> : d && (
        <>
          <KPIRow>
            <KPI label="Replicate spend" value={usd(d.total_usd)} hint={label} />
            <KPI label="Calls" value={int((d.areas || []).reduce((a: number, x: any) => a + x.calls, 0))} />
            <KPI label="Biggest cost" value={d.areas?.[0]?.what || '—'}
              hint={d.areas?.[0] ? `${usd(d.areas[0].usd)} · ${pct(d.areas[0].share)}` : ''} />
          </KPIRow>
          <Section title="What it paid for">
            <DataTable csvName="replicate_by_area" rows={d.areas || []} defaultSort="-usd"
              columns={['what', 'usd', 'share', 'calls', 'members', 'input_tokens', 'output_tokens']}
              optionalColumns={['exact_pct', 'failed']}
              labels={{ what: 'What', usd: 'Cost', share: 'Share', calls: 'Calls', members: 'Members',
                input_tokens: 'Input tokens', output_tokens: 'Output tokens',
                exact_pct: 'Tokens from Replicate', failed: 'Failed calls' }}
              render={{ usd: x => usd(x.usd), share: x => pct(x.share), calls: x => int(x.calls),
                input_tokens: x => int(x.input_tokens), output_tokens: x => int(x.output_tokens),
                exact_pct: x => pct(x.exact_pct), members: x => (x.members ? int(x.members) : '—') }}
              empty="No Replicate calls in this period." />
          </Section>
          <Section title="By model">
            <DataTable csvName="replicate_by_model" rows={d.models || []} defaultSort="-usd"
              columns={['model', 'usd', 'calls', 'input_tokens', 'output_tokens', 'price_in', 'price_out']}
              labels={{ model: 'Model', usd: 'Cost', calls: 'Calls', input_tokens: 'Input tokens',
                output_tokens: 'Output tokens', price_in: 'Input $/1M', price_out: 'Output $/1M' }}
              render={{ usd: x => (x.priced ? usd(x.usd) : 'no price'), calls: x => int(x.calls),
                input_tokens: x => int(x.input_tokens), output_tokens: x => int(x.output_tokens),
                price_in: x => (x.price_in ?? '—'), price_out: x => (x.price_out ?? '—') }} />
          </Section>
          <Section title="By month, from Replicate's record">
            <DataTable csvName="replicate_monthly" rows={d.monthly || []} defaultSort="-month"
              columns={['month', 'usd', 'calls', 'search_usd', 'ocean_usd', 'foryou_usd', 'enrichment_usd', 'other_usd']}
              labels={{ month: 'Month', usd: 'Cost', calls: 'Calls', search_usd: 'Cosmos search', ocean_usd: 'OCEAN',
                foryou_usd: 'For You & onboarding', enrichment_usd: 'Tech enrichment', other_usd: 'Other' }}
              render={{ usd: x => <b>{usd(x.usd)}</b>, calls: x => int(x.calls), search_usd: x => usd(x.search_usd),
                ocean_usd: x => usd(x.ocean_usd), foryou_usd: x => usd(x.foryou_usd),
                enrichment_usd: x => usd(x.enrichment_usd), other_usd: x => usd(x.other_usd) }}
              empty="Nothing read from Replicate yet." />
            {d.sync?.accounts?.some((a: any) => !a.history_complete) && (
              <div className="text-xs text-grayscale-1100">Older months are still loading from Replicate; they fill in every 10 minutes.</div>
            )}
          </Section>
          <Section title="Account use not logged by the site">
            <DataTable csvName="replicate_unlogged" rows={d.unlogged || []} defaultSort="-usd"
              columns={['category', 'model', 'source', 'predictions', 'usd']}
              labels={{ category: 'What', model: 'Model', source: 'Source', predictions: 'Predictions', usd: 'Cost' }}
              render={{ usd: x => usd(x.usd), predictions: x => int(x.predictions) }}
              empty="Every prediction on the account in this period was a logged site call." />
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Horizon
// ---------------------------------------------------------------------------
const RunKeyEditor: React.FC<{ run: any; keys: any[]; onDone: (saved: boolean) => void }> = ({ run, keys, onDone }) => {
  const [keyId, setKeyId] = useState(String(run.assigned_key_id || ''));
  const [note, setNote] = useState(run.note || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const opts = ['', ...keys.map(k => String(k.id))];
  const labels: Record<string, string> = { '': '— not set —' };
  keys.forEach(k => { labels[String(k.id)] = `${k.label || k.owner}${k.owner && k.label ? ` (${k.owner})` : ''}${k.last4 ? ` …${k.last4}` : ''}`; });
  const save = async () => {
    setBusy(true); setErr('');
    const { error } = await panelPost(S, 'run_key_set', { run_id: run.run_id, key_id: keyId || null, note });
    setBusy(false);
    if (error) setErr(error); else onDone(true);
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-primary-300 bg-primary-50 p-3">
      <div className="text-xs text-grayscale-1200">
        Run of {fmtDate(run.created_at)} · {int(run.techs)} techs · {usd(run.usd)}
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-xs text-grayscale-1100">Key</span>
        <Select value={keyId} onChange={setKeyId} options={opts} labels={labels} />
        <TextInput value={note} onChange={setNote} placeholder="Note, e.g. new universities, run on Pranav's laptop" wide />
        <Btn kind="primary" small onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Btn>
        <Btn small onClick={() => onDone(false)}>Cancel</Btn>
      </div>
      {keys.length === 0 && <div className="text-xs text-grayscale-1100">Add the key in Keys & prices first to pick it here.</div>}
      {err && <Msg kind="error">{err}</Msg>}
    </div>
  );
};

const Horizon: React.FC<TabProps> = ({ tick, bump, params, label }) => {
  const r = useAdminGet(S, 'horizon', params, [JSON.stringify(params), tick]);
  const d: any = r.data;
  const [editing, setEditing] = useState<any>(null);
  const [showAzure, setShowAzure] = useState(false);
  const runs = (d?.runs || []).filter((x: any) => showAzure || x.counted);
  const t = d?.totals || {};
  const keyText = (x: any) => {
    if (!x.key) return x.key_last4 ? `…${x.key_last4} (no note yet)` : '—';
    const k = x.key;
    return `${k.label || k.owner || k.provider}${k.owner && k.label ? ` · ${k.owner}` : ''}${k.last4 ? ` …${k.last4}` : ''}`;
  };
  return (
    <div className="flex flex-col gap-4">
      {r.error && <Msg kind="error">{r.error}</Msg>}
      {!d && r.loading ? <Loading /> : d && (
        <>
          <KPIRow>
            <KPI label="Horizon spend" value={usd(t.usd)} hint={label} />
            <KPI label="Runs" value={int(t.runs)} hint={d.manual_count ? `+ ${int(d.manual_count)} added by hand` : ''} />
            <KPI label="Techs" value={int(t.techs)} hint={t.techs ? `${usd(t.usd / t.techs)} per tech` : ''} />
            {t.manual_usd > 0 && <KPI label="Added by hand" value={usd(t.manual_usd)}
              hint={`${int(d.manual_count)} ${d.manual_count === 1 ? 'entry' : 'entries'} in Manual costs`} />}
          </KPIRow>
          {editing && (
            <RunKeyEditor run={editing} keys={(d.keys || []).filter((k: any) => k.id)}
              onDone={saved => { setEditing(null); if (saved) bump(); }} />
          )}
          <Section title="Runs" right={t.not_counted_runs > 0 ? (
            <label className="flex items-center gap-1.5 text-xs text-grayscale-1200 cursor-pointer">
              <input type="checkbox" checked={showAzure} onChange={e => setShowAzure(e.target.checked)} />
              Show {int(t.not_counted_runs)} Azure bulk-key {t.not_counted_runs === 1 ? 'run' : 'runs'} (not counted)
            </label>) : undefined}>
            <DataTable csvName="horizon_runs_cost" rows={runs} defaultSort="-created_at"
              columns={['created_at', 'usd', 'techs', 'per_tech', 'how', 'provider', 'key', 'note']}
              optionalColumns={['prompt', 'model', 'tokens', 'basis', 'machine', 'started_by', 'key_known_by', 'run_id']}
              labels={{ created_at: 'Started', usd: 'Cost', techs: 'Techs', per_tech: 'Per tech', how: 'Run from',
                provider: 'Provider', key: 'Key', note: 'Note', prompt: 'Prompt', model: 'Model', tokens: 'Tokens',
                basis: 'Cost basis', machine: 'Machine', started_by: 'Started by', key_known_by: 'Key from', run_id: 'Run id' }}
              render={{
                usd: x => (x.counted ? usd(x.usd)
                  : <span className="text-grayscale-1100" title="Azure bulk key: not counted">{usd(x.usd)} · not counted</span>),
                techs: x => int(x.techs), tokens: x => int(x.tokens), per_tech: x => usd(x.per_tech),
                provider: x => (x.provider === 'openai' ? 'OpenAI' : x.provider === 'azure' ? 'Azure OpenAI' : x.provider),
                key: x => <span title={x.key_known_by || ''}>{keyText(x)}</span>,
                note: x => x.note || '—',
              }}
              actions={x => <Btn small onClick={() => setEditing(x)}>Key & note</Btn>}
              empty="No Horizon runs in this period." />
          </Section>
          <Section title="By month">
            <DataTable csvName="horizon_monthly_cost" rows={d.monthly || []} defaultSort="-month"
              columns={['month', 'usd', 'runs', 'techs', 'tokens']}
              labels={{ month: 'Month', usd: 'Cost', runs: 'Runs', techs: 'Techs', tokens: 'Tokens' }}
              render={{ usd: x => usd(x.usd), techs: x => int(x.techs), tokens: x => int(x.tokens) }} />
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------
const Members: React.FC<TabProps> = ({ tick, params, label }) => {
  const r = useAdminGet(S, 'users', params, [JSON.stringify(params), tick]);
  const d: any = r.data;
  const rows = useMemo(() => (d?.rows || []).map((x: any, i: number) => ({ rank: i + 1, ...x })), [d]);
  const top = rows.find((x: any) => x.user_id);
  return (
    <div className="flex flex-col gap-4">
      {r.error && <Msg kind="error">{r.error}</Msg>}
      <div className="flex items-center gap-3 flex-wrap">
        {(d?.counted_from || d?.logging_since) && (
          <span className="text-xs text-grayscale-1100">Costs and searches counted from {fmtDate(d.counted_from || d.logging_since)}</span>
        )}
      </div>
      {!d && r.loading ? <Loading /> : d && (
        <>
          <KPIRow>
            <KPI label="Search spend" value={usd(d.total_usd)} hint={`Cosmos + OCEAN searches · ${label}`} />
            <KPI label="Members who searched" value={int(d.members)} />
            <KPI label="Uses the most" value={top ? (top.name || top.email || top.user_id) : '—'}
              hint={top ? `${usd(top.usd)} · ${pct(top.share)} of search spend` : ''} />
          </KPIRow>
          <Section title="Who uses the most">
            <DataTable csvName="api_spend_members" rows={rows} defaultSort="-usd" searchable
              columns={['rank', 'member', 'usd', 'share', 'searches', 'ocean_searches', 'per_search', 'calls', 'search_usd', 'ocean_usd']}
              labels={{ rank: '#', member: 'Member', usd: 'Cost', share: 'Share', searches: 'Cosmos searches',
                ocean_searches: 'OCEAN searches', per_search: 'Per search', calls: 'AI calls',
                search_usd: 'Cosmos cost', ocean_usd: 'OCEAN cost' }}
              render={{
                rank: x => (x.rank <= 3 ? <b>{x.rank}</b> : x.rank),
                member: x => (x.user_id ? (
                  <span className="flex flex-col">
                    <AdminLink to={{ group: 'user360', email: x.email || x.user_id }}>{x.name || x.email || x.user_id}</AdminLink>
                    {x.name && x.email ? <span className="text-[11px] text-grayscale-1100">{x.email}</span> : null}
                  </span>
                ) : <span className="text-grayscale-1100">{x.email}</span>),
                usd: x => <b>{usd(x.usd)}</b>, share: x => pct(x.share), searches: x => int(x.searches),
                ocean_searches: x => int(x.ocean_searches), per_search: x => usdPer(x.per_search), calls: x => int(x.calls),
                search_usd: x => usd(x.search_usd), ocean_usd: x => usd(x.ocean_usd),
              }}
              empty="No searches in this period." />
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per search
// ---------------------------------------------------------------------------
const STAT_COLS = ['label', 'searches', 'with_ai', 'avg', 'avg_with_ai', 'max', 'per_1000', 'avg_calls', 'avg_ms', 'usd'];
const statLabels = (what: string) => ({
  label: what, searches: 'Searches', with_ai: 'Used AI', avg: 'Avg per search', avg_with_ai: 'Avg when AI ran',
  max: 'Most', per_1000: 'Per 1,000', avg_calls: 'AI calls each', avg_ms: 'Avg time', usd: 'Total',
});
const statRender = {
  searches: (x: any) => int(x.searches), with_ai: (x: any) => int(x.with_ai), avg: (x: any) => <b>{usdPer(x.avg)}</b>,
  avg_with_ai: (x: any) => usdPer(x.avg_with_ai), max: (x: any) => usdPer(x.max), per_1000: (x: any) => usd(x.per_1000),
  avg_calls: (x: any) => (x.avg_calls === null || x.avg_calls === undefined ? '—' : x.avg_calls),
  avg_ms: (x: any) => (x.avg_ms === null || x.avg_ms === undefined ? '—' : x.avg_ms >= 1000 ? `${(x.avg_ms / 1000).toFixed(1)} s` : `${x.avg_ms} ms`),
  usd: (x: any) => usdPer(x.usd),
};

const PerSearch: React.FC<TabProps> = ({ tick, params, label }) => {
  const r = useAdminGet(S, 'per_search', params, [JSON.stringify(params), tick]);
  const d: any = r.data;
  const total = d?.cosmos?.total;
  const ocean = (d?.ocean?.scenarios || []).find((x: any) => x.key === 'ocean_search');
  const lastDay = [...(d?.daily || [])].reverse().find((x: any) => x.cosmos_running_avg !== null);
  const notTied = d?.cosmos?.not_tied;
  return (
    <div className="flex flex-col gap-4">
      {r.error && <Msg kind="error">{r.error}</Msg>}
      {d?.counted_from && <span className="text-xs text-grayscale-1100">Counted from {fmtDate(d.counted_from)}</span>}
      {!d && r.loading ? <Loading /> : d && (!d.cosmos ? (
        <span className="text-sm text-grayscale-1100">No searches with cost tracking in this period.</span>
      ) : (
        <>
          <KPIRow>
            <KPI label="Avg per Cosmos search" value={usdPer(total?.avg)} hint={`${int(total?.searches)} searches · ${label}`} />
            <KPI label="When the AI runs" value={usdPer(total?.avg_with_ai)}
              hint={`${int(total?.with_ai)} of ${int(total?.searches)} searches used AI`} />
            <KPI label="Per 1,000 Cosmos searches" value={usd(total?.per_1000)} hint="at the period's mix" />
            <KPI label="Running average" value={usdPer(lastDay?.cosmos_running_avg)}
              hint={lastDay ? `Cosmos, to ${fmtDay(lastDay.day)}` : ''} />
            <KPI label="Avg per OCEAN search" value={usdPer(ocean?.avg)} hint={`${int(ocean?.searches)} searches`} />
          </KPIRow>
          <Section title="Cosmos search, by kind">
            <DataTable csvName="cost_per_search_kind" rows={d.cosmos.scenarios || []} defaultSort="-searches"
              columns={STAT_COLS} labels={statLabels('Kind of search')} render={statRender}
              empty="No Cosmos searches in this period." />
            {notTied?.calls > 0 && (
              <span className="text-xs text-grayscale-1100">
                {int(notTied.calls)} Cosmos AI calls ({usdPer(notTied.usd)}) are not tied to a logged search and are left out of these averages.
              </span>
            )}
          </Section>
          <Section title="Members and signed-out visitors">
            <DataTable csvName="cost_per_search_who" rows={d.cosmos.who || []} defaultSort="-searches"
              columns={STAT_COLS} labels={statLabels('Who')} render={statRender} />
          </Section>
          <Section title="OCEAN">
            <DataTable csvName="cost_per_search_ocean" rows={d.ocean?.scenarios || []} defaultSort="-searches"
              columns={STAT_COLS} labels={{ ...statLabels('What'), searches: 'Searches / matches' }} render={statRender} />
          </Section>
          <Section title="By day, with running averages">
            <DataTable csvName="cost_per_search_daily" rows={d.daily || []} defaultSort="-day"
              columns={['day', 'cosmos_searches', 'cosmos_usd', 'cosmos_avg', 'cosmos_running_avg', 'cosmos_avg_7d',
                'ocean_searches', 'ocean_usd', 'ocean_avg', 'ocean_running_avg']}
              labels={{ day: 'Day', cosmos_searches: 'Cosmos searches', cosmos_usd: 'Cosmos cost', cosmos_avg: 'Avg that day',
                cosmos_running_avg: 'Running avg', cosmos_avg_7d: 'Last 7 days avg', ocean_searches: 'OCEAN searches',
                ocean_usd: 'OCEAN cost', ocean_avg: 'OCEAN avg that day', ocean_running_avg: 'OCEAN running avg' }}
              render={{
                day: x => fmtDay(x.day), cosmos_searches: x => int(x.cosmos_searches), cosmos_usd: x => usdPer(x.cosmos_usd),
                cosmos_avg: x => usdPer(x.cosmos_avg), cosmos_running_avg: x => <b>{usdPer(x.cosmos_running_avg)}</b>,
                cosmos_avg_7d: x => usdPer(x.cosmos_avg_7d), ocean_searches: x => int(x.ocean_searches),
                ocean_usd: x => usdPer(x.ocean_usd), ocean_avg: x => usdPer(x.ocean_avg),
                ocean_running_avg: x => usdPer(x.ocean_running_avg),
              }} />
          </Section>
        </>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Keys & prices
// ---------------------------------------------------------------------------
const PROVIDERS = ['replicate', 'openai', 'azure', 'aws', 'other'];
const PROVIDER_LABELS: Record<string, string> = { replicate: 'Replicate', openai: 'OpenAI', azure: 'Azure OpenAI', aws: 'AWS', other: 'Other' };
const BLANK = { id: null, provider: 'replicate', fp: '', last4: '', label: '', owner: '', used_for: '', notes: '' };

const KeyForm: React.FC<{ start: any; onDone: (saved: boolean) => void }> = ({ start, onDone }) => {
  const [f, setF] = useState<any>({ ...BLANK, ...start });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: string) => (v: string) => setF((x: any) => ({ ...x, [k]: v }));
  const fromServer = !!start.on_server;
  const save = async () => {
    setBusy(true); setErr('');
    const { error } = await panelPost(S, 'key_save', {
      id: f.id || undefined, provider: f.provider, fp: f.fp || undefined, last4: f.last4 || undefined,
      label: f.label, owner: f.owner, used_for: f.used_for, notes: f.notes,
    });
    setBusy(false);
    if (error) setErr(error); else onDone(true);
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-primary-300 bg-primary-50 p-3">
      <div className="text-xs text-grayscale-1200">
        {fromServer ? <>Key on this server{f.where ? ` · ${f.where}` : ''} · ends …{f.last4}</>
          : <>A key used elsewhere (a laptop, another server). Its last 4 characters match it to Horizon runs that recorded them.</>}
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        {!fromServer && <Select value={f.provider} onChange={set('provider')} options={PROVIDERS} labels={PROVIDER_LABELS} />}
        <TextInput value={f.label || ''} onChange={set('label')} placeholder="Label, e.g. Pranav's OpenAI key" />
        <TextInput value={f.owner || ''} onChange={set('owner')} placeholder="Owner / who pays" />
        {!fromServer && <TextInput value={f.last4 || ''} onChange={set('last4')} placeholder="Last 4 characters" />}
      </div>
      <div className="flex">
        <TextInput value={f.used_for || ''} onChange={set('used_for')} placeholder="Used for, e.g. Horizon new-university runs" wide />
      </div>
      <textarea value={f.notes || ''} onChange={e => set('notes')(e.target.value)} rows={2}
        placeholder="Notes: account, billing, when to rotate…"
        className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
      <div className="flex gap-2">
        <Btn kind="primary" small onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Btn>
        <Btn small onClick={() => onDone(false)}>Cancel</Btn>
      </div>
      {err && <Msg kind="error">{err}</Msg>}
    </div>
  );
};

const PriceEditor: React.FC<{ rows: any[]; onSaved: () => void }> = ({ rows, onSaved }) => {
  const [vals, setVals] = useState<Record<string, { in: string; out: string }>>({});
  const [model, setModel] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => {
    const v: Record<string, { in: string; out: string }> = {};
    rows.forEach(r => { v[r.model] = { in: r.in ?? '', out: r.out ?? '' }; });
    setVals(v);
  }, [rows]);
  const changed = Object.entries(vals).filter(([m, v]) => {
    const r = rows.find(x => x.model === m);
    return !r || String(r.in ?? '') !== String(v.in) || String(r.out ?? '') !== String(v.out);
  });
  const save = async () => {
    setBusy(true); setMsg(null);
    const prices: Record<string, any> = {};
    changed.forEach(([m, v]) => { if (v.in !== '' && v.out !== '') prices[m] = { in: Number(v.in), out: Number(v.out) }; });
    const { error } = await panelPost(S, 'prices_set', { prices });
    setBusy(false);
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: 'Prices saved; every total is worked out again with them.' }); onSaved(); }
  };
  const all = [...rows.map(r => r.model), ...Object.keys(vals).filter(m => !rows.some(r => r.model === m))];
  return (
    <div className="flex flex-col gap-2">
      <table className="text-sm">
        <thead>
          <tr className="text-left text-xs text-grayscale-1100">
            <th className="py-1 pr-3 font-normal">Model</th><th className="py-1 pr-3 font-normal">Input, USD per 1M tokens</th>
            <th className="py-1 font-normal">Output, USD per 1M tokens</th>
          </tr>
        </thead>
        <tbody>
          {all.map(m => (
            <tr key={m} className="border-t border-grayscale-200">
              <td className="py-1 pr-3 text-grayscale-1300">{m}{!(rows.find(r => r.model === m)?.in ?? null) && rows.find(r => r.model === m)
                ? <span className="text-amber-700 text-xs"> · no price</span> : null}</td>
              {(['in', 'out'] as const).map(side => (
                <td key={side} className="py-1 pr-3">
                  <input value={vals[m]?.[side] ?? ''} inputMode="decimal"
                    onChange={e => setVals(v => ({ ...v, [m]: { ...(v[m] || { in: '', out: '' }), [side]: e.target.value } }))}
                    className="h-8 w-28 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex gap-2 items-center flex-wrap">
        <TextInput value={model} onChange={setModel} placeholder="owner/model" />
        <Btn small disabled={!model.includes('/')} onClick={() => {
          setVals(v => ({ ...v, [model.trim()]: v[model.trim()] || { in: '', out: '' } })); setModel('');
        }}>Add model</Btn>
        <Btn kind="primary" small onClick={save} disabled={busy || changed.length === 0}>
          {busy ? 'Saving…' : `Save prices${changed.length ? ` (${changed.length})` : ''}`}
        </Btn>
      </div>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
    </div>
  );
};

const Keys: React.FC<{ tick: number; bump: () => void }> = ({ tick, bump }) => {
  const r = useAdminGet(S, 'keys', {}, [tick]);
  const d: any = r.data;
  const [form, setForm] = useState<any>(null);
  const [err, setErr] = useState('');
  const del = async (id: number) => {
    setErr('');
    const { error } = await panelPost(S, 'key_delete', { id });
    if (error) setErr(error); else bump();
  };
  return (
    <div className="flex flex-col gap-4">
      {(r.error || err) && <Msg kind="error">{r.error || err}</Msg>}
      {!d && r.loading ? <Loading /> : d && (
        <>
          <Section title="API keys" right={<Btn small onClick={() => setForm({ ...BLANK })}>Add a key used elsewhere</Btn>}>
            {form && <KeyForm start={form} onDone={saved => { setForm(null); if (saved) bump(); }} />}
            <DataTable csvName="api_keys" rows={d.keys || []}
              columns={['provider', 'label', 'owner', 'used_for', 'last4', 'where', 'notes']}
              optionalColumns={['fp', 'updated_by', 'updated_at']}
              labels={{ provider: 'Provider', label: 'Label', owner: 'Owner', used_for: 'Used for', last4: 'Ends with',
                where: 'On this server', notes: 'Notes', fp: 'Fingerprint', updated_by: 'Updated by', updated_at: 'Updated' }}
              render={{
                provider: x => PROVIDER_LABELS[x.provider] || x.provider,
                label: x => x.label || (x.id ? '—' : <span className="text-amber-700">no note yet</span>),
                last4: x => (x.last4 ? `…${x.last4}` : '—'),
                where: x => (x.on_server ? (x.where || 'yes') : 'no'),
              }}
              actions={x => (
                <span className="flex gap-1">
                  <Btn small onClick={() => setForm(x)}>{x.id ? 'Edit' : 'Add note'}</Btn>
                  {x.id ? <DangerBtn small onConfirm={() => del(x.id)}>Delete</DangerBtn> : null}
                </span>
              )} />
          </Section>
          <Section title="Replicate prices">
            <PriceEditor rows={d.prices || []} onSaved={bump} />
          </Section>
        </>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Manual costs
// ---------------------------------------------------------------------------
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const BLANK_COST = { id: null, day: '', amount_usd: '', category: 'enrichment', provider: 'openai', key_id: '', techs: '', description: '' };

const CostForm: React.FC<{ start: any; cats: any[]; keys: any[]; onDone: (saved: boolean) => void }> =
  ({ start, cats, keys, onDone }) => {
  const [f, setF] = useState<any>({ ...BLANK_COST, ...start, day: start.day || today(),
    key_id: start.key_id ? String(start.key_id) : '', techs: start.techs ?? '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: string) => (v: string) => setF((x: any) => ({ ...x, [k]: v }));
  const catOpts = cats.map(c => c.key);
  const catLabels = Object.fromEntries(cats.map(c => [c.key, c.label]));
  const keyOpts = ['', ...keys.filter(k => k.id).map(k => String(k.id))];
  const keyLabels: Record<string, string> = { '': 'Key: not set' };
  keys.forEach(k => { if (k.id) keyLabels[String(k.id)] = `${k.label || k.owner}${k.last4 ? ` …${k.last4}` : ''}`; });
  const counts = f.category === 'horizon' ? 'Horizon' : f.provider === 'replicate' ? 'Replicate' : 'Other';
  const save = async () => {
    setBusy(true); setErr('');
    const { error } = await panelPost(S, 'manual_save', {
      id: f.id || undefined, day: f.day, amount_usd: f.amount_usd, category: f.category, provider: f.provider,
      key_id: f.key_id || null, techs: f.techs === '' ? null : f.techs, description: f.description,
    });
    setBusy(false);
    if (error) setErr(error); else onDone(true);
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-primary-300 bg-primary-50 p-3">
      <div className="flex gap-2 flex-wrap items-center">
        <input type="date" value={f.day} max={today()} onChange={e => set('day')(e.target.value)}
          className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
        <TextInput value={String(f.amount_usd)} onChange={set('amount_usd')} placeholder="Amount, USD" />
        <Select value={f.category} onChange={set('category')} options={catOpts} labels={catLabels} />
        <Select value={f.provider} onChange={set('provider')} options={PROVIDERS} labels={PROVIDER_LABELS} />
        <Select value={f.key_id} onChange={set('key_id')} options={keyOpts} labels={keyLabels} />
        <TextInput value={String(f.techs)} onChange={set('techs')} placeholder="Techs (optional)" />
      </div>
      <div className="flex">
        <TextInput value={f.description} onChange={set('description')} wide
          placeholder="What it was for, e.g. Tech enrichment + Horizon for Northwestern and TCD" />
      </div>
      <div className="flex gap-2 items-center flex-wrap">
        <Btn kind="primary" small onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Btn>
        <Btn small onClick={() => onDone(false)}>Cancel</Btn>
        <span className="text-xs text-grayscale-1100">Counts under {counts} in the totals.</span>
      </div>
      {err && <Msg kind="error">{err}</Msg>}
    </div>
  );
};

const ManualCosts: React.FC<{ tick: number; bump: () => void }> = ({ tick, bump }) => {
  const r = useAdminGet(S, 'manual', {}, [tick]);
  const d: any = r.data;
  const [form, setForm] = useState<any>(null);
  const [err, setErr] = useState('');
  const del = async (id: number) => {
    setErr('');
    const { error } = await panelPost(S, 'manual_delete', { id });
    if (error) setErr(error); else bump();
  };
  return (
    <div className="flex flex-col gap-4">
      {(r.error || err) && <Msg kind="error">{r.error || err}</Msg>}
      {!d && r.loading ? <Loading /> : d && (
        <>
          <KPIRow>
            <KPI label="Added by hand" value={usd(d.total_usd)} hint={`${int(d.rows?.length || 0)} entries`} />
          </KPIRow>
          <Section title="Manual costs" right={<Btn small kind="primary" onClick={() => setForm({ ...BLANK_COST })}>Add a cost</Btn>}>
            {form && <CostForm start={form} cats={d.categories || []} keys={d.keys || []}
              onDone={saved => { setForm(null); if (saved) bump(); }} />}
            <DataTable csvName="api_spend_manual" rows={d.rows || []} defaultSort="-day" searchable
              columns={['day', 'amount_usd', 'category_label', 'description', 'techs', 'per_tech', 'provider', 'key', 'counts_under']}
              optionalColumns={['created_by', 'updated_by', 'updated_at']}
              labels={{ day: 'Date', amount_usd: 'Amount', category_label: 'For', description: 'Description', techs: 'Techs',
                per_tech: 'Per tech', provider: 'Provider', key: 'Key', counts_under: 'Counts under',
                created_by: 'Added by', updated_by: 'Updated by', updated_at: 'Updated' }}
              render={{
                day: x => new Date(`${x.day}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
                amount_usd: x => <b>{usd(x.amount_usd)}</b>, techs: x => int(x.techs), per_tech: x => usd(x.per_tech),
                provider: x => PROVIDER_LABELS[x.provider] || x.provider || '—',
                key: x => (x.key ? `${x.key.label || x.key.owner}${x.key.last4 ? ` …${x.key.last4}` : ''}` : '—'),
              }}
              actions={x => (
                <span className="flex gap-1">
                  <Btn small onClick={() => setForm(x)}>Edit</Btn>
                  <DangerBtn small onConfirm={() => del(x.id)}>Delete</DangerBtn>
                </span>
              )}
              empty="No costs added by hand yet." />
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Credits
// ---------------------------------------------------------------------------
const CREDIT_PROVIDERS = ['replicate', 'openai', 'aws'];
const plusYear = (iso: string) => (iso ? `${Number(iso.slice(0, 4)) + 1}${iso.slice(4)}` : '');

const TopUpForm: React.FC<{ start: any; onDone: (saved: boolean) => void }> = ({ start, onDone }) => {
  const day0 = start.day || istToday();
  const [f, setF] = useState<any>({ provider: 'replicate', note: '', ...start, day: day0,
    amount_usd: start.amount_usd ?? '', expires_on: start.id ? (start.expires_on || '') : plusYear(day0) });
  const [never, setNever] = useState(!!start.id && !start.expires_on);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: string) => (v: string) => setF((x: any) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true); setErr('');
    const { error } = await panelPost(S, 'credit_save', {
      id: f.id || undefined, provider: f.provider, day: f.day, amount_usd: f.amount_usd,
      expires_on: never ? null : (f.expires_on || null), note: f.note,
    });
    setBusy(false);
    if (error) setErr(error); else onDone(true);
  };
  const dateCls = 'h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm';
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-primary-300 bg-primary-50 p-3">
      <div className="flex gap-2 flex-wrap items-center">
        <Select value={f.provider} onChange={set('provider')} options={CREDIT_PROVIDERS} labels={PROVIDER_LABELS} />
        <span className="text-xs text-grayscale-1100">Bought</span>
        <input type="date" value={f.day} max={istToday()} className={dateCls}
          onChange={e => setF((x: any) => ({ ...x, day: e.target.value, expires_on: x.id ? x.expires_on : plusYear(e.target.value) }))} />
        <TextInput value={String(f.amount_usd)} onChange={set('amount_usd')} placeholder="Amount, USD" />
        <span className="text-xs text-grayscale-1100">Expires</span>
        <input type="date" value={never ? '' : f.expires_on} disabled={never} min={f.day} className={dateCls}
          onChange={e => set('expires_on')(e.target.value)} />
        <label className="flex items-center gap-1 text-xs text-grayscale-1200">
          <input type="checkbox" checked={never} onChange={e => setNever(e.target.checked)} /> Never expires
        </label>
      </div>
      <div className="flex">
        <TextInput value={f.note || ''} onChange={set('note')} wide placeholder="Note, e.g. invoice number, who paid" />
      </div>
      <div className="flex gap-2">
        <Btn kind="primary" small onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Btn>
        <Btn small onClick={() => onDone(false)}>Cancel</Btn>
      </div>
      {err && <Msg kind="error">{err}</Msg>}
    </div>
  );
};

const Credits: React.FC<{ tick: number; bump: () => void }> = ({ tick, bump }) => {
  const r = useAdminGet(S, 'credits', {}, [tick]);
  const d: any = r.data;
  const [form, setForm] = useState<any>(null);
  const [err, setErr] = useState('');
  const del = async (id: number) => {
    setErr('');
    const { error } = await panelPost(S, 'credit_delete', { id });
    if (error) setErr(error); else bump();
  };
  const providers: any[] = d?.providers || [];
  return (
    <div className="flex flex-col gap-4">
      {(r.error || err) && <Msg kind="error">{r.error || err}</Msg>}
      <div className="flex justify-end">
        <Btn kind="primary" small onClick={() => setForm({})}>Add a top-up</Btn>
      </div>
      {form && <TopUpForm start={form} onDone={saved => { setForm(null); if (saved) bump(); }} />}
      {!d && r.loading ? <Loading /> : d && providers.length === 0 ? (
        <Section title="Credits">
          <div className="text-sm text-grayscale-1100 py-4 text-center">No top-ups yet. Add each credit purchase to track what is left.</div>
        </Section>
      ) : providers.map(p => (
        <Section key={p.provider} title={`${PROVIDER_LABELS[p.provider] || p.provider} credit`}>
          <KPIRow>
            <KPI label="Remaining" value={usd(p.balance)} hint={`of ${usd(p.bought)} bought`} />
            <KPI label="Used" value={usd(p.used)} />
            <KPI label="Expired unused" value={usd(p.expired)} />
            <KPI label="Lasts about" value={p.days_left === null ? '—' : p.days_left > 730 ? '2+ years' : `${int(p.days_left)} days`}
              hint={p.avg_daily ? `at ${usd(p.avg_daily)}/day (last ${d.recent_days} days)${p.runs_out_on && p.days_left <= 730 ? ` · until ${fmtDay(p.runs_out_on)}` : ''}` : 'no spend in the last 2 weeks'} />
          </KPIRow>
          {p.uncovered > 0 && (
            <div className="text-xs text-amber-700">
              {usd(p.uncovered)} was spent while no credit was left; check whether it was billed to the card.
            </div>
          )}
          <DataTable csvName={`credits_${p.provider}`} rows={p.lots || []}
            columns={['day', 'amount_usd', 'used', 'expired', 'left', 'expires_on', 'status', 'note']}
            optionalColumns={['created_by', 'updated_at']}
            labels={{ day: 'Bought', amount_usd: 'Amount', used: 'Used', expired: 'Expired', left: 'Left',
              expires_on: 'Expires', status: 'Status', note: 'Note', created_by: 'Added by', updated_at: 'Updated' }}
            render={{
              day: x => fmtDay(x.day), expires_on: x => (x.expires_on ? fmtDay(x.expires_on) : 'never'),
              amount_usd: x => usd(x.amount_usd), used: x => usd(x.used), expired: x => usd(x.expired),
              left: x => <b>{usd(x.left)}</b>, note: x => x.note || '—',
            }}
            actions={x => (
              <span className="flex gap-1">
                <Btn small onClick={() => setForm({ ...x, provider: p.provider })}>Edit</Btn>
                <DangerBtn small onConfirm={() => del(x.id)}>Delete</DangerBtn>
              </span>
            )} />
        </Section>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
const SpendPanel: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const start = TAB_PARAM[String(target?.tab || '').toLowerCase()] || TABS[0];
  const [tab, setTab] = useState(start);
  const [seen, setSeen] = useState<Record<string, boolean>>({ [start]: true });
  const pick = useCallback((t: string) => { setTab(t); setSeen(v => (v[t] ? v : { ...v, [t]: true })); }, []);
  useEffect(() => {
    const t = TAB_PARAM[String(target?.tab || '').toLowerCase()];
    if (t) pick(t);
  }, [target?.nonce, target?.tab, pick]);
  const [tick, setTick] = useState(0);
  const bump = useCallback(() => setTick(x => x + 1), []);
  const [period, setPeriod] = useState<Period>({ preset: '30d', from: '', to: '' });
  const [params, setParams] = useState<Record<string, string>>(() => periodParams({ preset: '30d', from: '', to: '' }));
  useEffect(() => { if (!periodError(period)) setParams(periodParams(period)); }, [period]);
  const label = periodText(period, PRESET_LABELS);
  const tp = { tick, bump, params, label };
  const body: Record<string, React.ReactNode> = {
    'Overview': <Overview {...tp} />,
    'Replicate': <Replicate {...tp} />,
    'Horizon': <Horizon {...tp} />,
    'Members': <Members {...tp} />,
    'Per search': <PerSearch {...tp} />,
    'Manual costs': <ManualCosts tick={tick} bump={bump} />,
    'Credits': <Credits tick={tick} bump={bump} />,
    'Keys & prices': <Keys tick={tick} bump={bump} />,
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <SubTabs tabs={TABS} active={tab} onChange={pick} />
        {!['Manual costs', 'Credits', 'Keys & prices'].includes(tab) && (
          <PeriodPicker value={period} onChange={setPeriod} options={PRESETS} labels={PRESET_LABELS}
            presetDays={PRESET_DAYS} today={istToday} hint="Both days included (India time)." />
        )}
      </div>
      {TABS.filter(t => seen[t]).map(t => (
        <div key={t} className={t === tab ? '' : 'hidden'}>{body[t]}</div>
      ))}
    </div>
  );
};

export default SpendPanel;
